import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog.tsx';
import { Label } from '@/components/ui/label.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import {
    Zap, Grid3x3, Monitor, Smartphone, Globe, BarChart3,
    CheckCircle2, Settings, Archive, XCircle
} from 'lucide-react';
import {
    generateBlocks,
    getDomains,
    getDomainsWithBlocks,
    getSummary,
    updateBlockStatus,
    updateRootStatus
} from "@/services/api";
import {GenerateBlocksRequest} from "@/types/project.ts";


export default function Blocos() {
    const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
    const [isUpdateBlockDialogOpen, setIsUpdateBlockDialogOpen] = useState(false);
    const [isUpdateRootDialogOpen, setIsUpdateRootDialogOpen] = useState(false);
    const [selectedDomainId, setSelectedDomainId] = useState<number>(0);
    const [selectedBlockType, setSelectedBlockType] = useState<string>('ALL');
    const [selectedFormatType, setSelectedFormatType] = useState<string>('BOTH');
    const [selectedBlock, setSelectedBlock] = useState<any>(null);
    const [selectedRoot, setSelectedRoot] = useState<any>(null);
    const [selectedStatus, setSelectedStatus] = useState<string>('ACTIVE');
    const [updateRootAndChildren, setUpdateRootAndChildren] = useState(true);

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const blockTypes = [
        { value: 'ALL', label: 'Todos os Blocos (15)', icon: Grid3x3 },
        { value: 'Topo', label: 'Topo', icon: null },
        { value: 'Content1', label: 'Content 1', icon: null },
        { value: 'Content2', label: 'Content 2', icon: null },
        { value: 'Rewarded', label: 'Rewarded', icon: null },
        { value: 'Interstitial', label: 'Interstitial', icon: null },
        { value: 'OfferWall', label: 'OfferWall', icon: null },
        { value: 'Trigger', label: 'Trigger', icon: null },
        { value: 'Anchor', label: 'Anchor', icon: null },
        { value: 'Fixed', label: 'Fixed', icon: null },
    ];

    const formatTypes = [
        { value: 'BOTH', label: 'Mobile + Web', icon: Globe },
        { value: 'MOBILE', label: 'Apenas Mobile', icon: Smartphone },
        { value: 'WEB', label: 'Apenas Web', icon: Monitor },
    ];

    const statusOptions = [
        { value: 'ACTIVE', label: 'Ativo', icon: CheckCircle2, color: 'text-green-600' },
        { value: 'INACTIVE', label: 'Inativo', icon: XCircle, color: 'text-gray-600' },
        { value: 'ARCHIVED', label: 'Arquivado', icon: Archive, color: 'text-red-600' },
    ];

    const { data: domains } = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
    });

    const { data: summary, isLoading: isLoadingSummary } = useQuery({
        queryKey: ['adUnitSummary'],
        queryFn: getSummary,
    });

    const { data: domainsWithBlocks } = useQuery({
        queryKey: ['domainsWithBlocks'],
        queryFn: getDomainsWithBlocks,
    });

    const generateMutation = useMutation({
        mutationFn: generateBlocks,
        onSuccess: (data) => {
            toast({
                title: 'Blocos gerados com sucesso!',
                description: `${data.blocks_created || 0} blocos foram criados no GAM.`,
            });
            queryClient.invalidateQueries({ queryKey: ['adUnitSummary'] });
            queryClient.invalidateQueries({ queryKey: ['domainsWithBlocks'] });
            setIsGenerateDialogOpen(false);
            resetForm();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao gerar blocos',
                description: error.message || 'Ocorreu um erro ao gerar os blocos.',
            });
        },
    });

    const updateBlockMutation = useMutation({
        mutationFn: ({ blockId, status }: { blockId: number; status: string }) =>
            updateBlockStatus(blockId, status),
        onSuccess: () => {
            toast({
                title: 'Status atualizado!',
                description: 'O status do bloco foi atualizado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['adUnitSummary'] });
            queryClient.invalidateQueries({ queryKey: ['domainsWithBlocks'] });
            setIsUpdateBlockDialogOpen(false);
            setSelectedBlock(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar status',
                description: error.message || 'Ocorreu um erro ao atualizar o status.',
            });
        },
    });

    const updateRootMutation = useMutation({
        mutationFn: ({ rootId, status, updateRoot }: { rootId: number; status: string; updateRoot: boolean }) =>
            updateRootStatus(rootId, status, updateRoot),
        onSuccess: (_, variables) => {
            toast({
                title: 'Status atualizado!',
                description: variables.updateRoot
                    ? 'O status do root e dos blocos filhos foi atualizado.'
                    : 'O status dos blocos filhos foi atualizado.',
            });
            queryClient.invalidateQueries({ queryKey: ['adUnitSummary'] });
            queryClient.invalidateQueries({ queryKey: ['domainsWithBlocks'] });
            setIsUpdateRootDialogOpen(false);
            setSelectedRoot(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar status',
                description: error.message || 'Ocorreu um erro ao atualizar o status.',
            });
        },
    });

    const resetForm = () => {
        setSelectedDomainId(0);
        setSelectedBlockType('ALL');
        setSelectedFormatType('BOTH');
    };

    const handleGenerate = () => {
        if (!selectedDomainId) {
            toast({
                variant: 'destructive',
                title: 'Domínio obrigatório',
                description: 'Por favor, selecione um domínio.',
            });
            return;
        }

        const request: GenerateBlocksRequest = {
            domain_id: selectedDomainId,
        };

        if (selectedBlockType !== 'ALL') {
            request.block_type = selectedBlockType;

            if (selectedFormatType !== 'BOTH') {
                request.format_type = selectedFormatType as 'MOBILE' | 'WEB';
            }
        }

        generateMutation.mutate(request);
    };

    const handleUpdateBlockStatus = () => {
        if (!selectedBlock) return;
        updateBlockMutation.mutate({
            blockId: selectedBlock.id,
            status: selectedStatus,
        });
    };

    const handleUpdateRootStatus = () => {
        if (!selectedRoot) return;
        updateRootMutation.mutate({
            rootId: selectedRoot.id,
            status: selectedStatus,
            updateRoot: updateRootAndChildren,
        });
    };

    const openBlockStatusDialog = (block: any) => {
        setSelectedBlock(block);
        setSelectedStatus(block.ad_unit_status);
        setIsUpdateBlockDialogOpen(true);
    };

    const openRootStatusDialog = (root: any) => {
        setSelectedRoot(root);
        setSelectedStatus(root.status);
        setUpdateRootAndChildren(true);
        setIsUpdateRootDialogOpen(true);
    };

    const getStatusBadge = (status: string) => {
        const variants: Record<string, any> = {
            ACTIVE: { variant: 'success', label: 'Ativo' },
            INACTIVE: { variant: 'secondary', label: 'Inativo' },
            ARCHIVED: { variant: 'destructive', label: 'Arquivado' },
        };
        const config = variants[status] || variants.INACTIVE;
        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    return (
        <div className="space-y-4 md:space-y-6 p-4 md:p-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Blocos GAM</h1>
                    <p className="mt-1 md:mt-2 text-sm md:text-base text-gray-600">
                        Gerencie blocos de anúncios do Google Ad Manager
                    </p>
                </div>
                <Button
                    onClick={() => setIsGenerateDialogOpen(true)}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 w-full sm:w-auto"
                >
                    <Zap className="mr-2 h-4 w-4" />
                    Gerar Blocos
                </Button>
            </div>

            {/* Info Card */}
            <div className="rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 p-4 md:p-6">
                <div className="flex items-start gap-3 md:gap-4">
                    <div className="flex-shrink-0 h-10 w-10 md:h-12 md:w-12 bg-green-600 rounded-full flex items-center justify-center">
                        <Grid3x3 className="h-5 w-5 md:h-6 md:w-6 text-white" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-green-900 text-sm md:text-base">Como funciona?</h3>
                        <p className="text-xs md:text-sm text-green-700 mt-1">
                            Gere automaticamente blocos de anúncios no GAM. Escolha gerar todos os 15 blocos ou selecione blocos específicos para Mobile e/ou Web.
                        </p>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            {isLoadingSummary && (
                <div className="rounded-lg bg-white p-6 shadow">
                    <p className="text-center text-gray-600">Carregando resumo...</p>
                </div>
            )}

            {summary && summary.length > 0 && (
                <div className="grid gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {summary.map((item) => {
                        const mobileBlocks = Math.ceil(item.total_blocks / 2);
                        const webBlocks = Math.floor(item.total_blocks / 2);
                        const hasRoot = !!item.root_id;

                        return (
                            <div
                                key={item.domain_id}
                                className="rounded-lg bg-white p-4 md:p-6 shadow border border-gray-100 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-gray-900 mb-1 truncate text-sm md:text-base">
                                            {item.domain_name}
                                        </h3>
                                        <p className="text-xs text-gray-500 truncate">Network: {item.network}</p>
                                    </div>
                                    {hasRoot ? (
                                        <Badge variant="success" className="flex items-center gap-1 flex-shrink-0 ml-2">
                                            <CheckCircle2 className="h-3 w-3" />
                                            <span className="hidden sm:inline">Configurado</span>
                                        </Badge>
                                    ) : (
                                        <Badge variant="secondary" className="flex-shrink-0 ml-2">Sem Blocos</Badge>
                                    )}
                                </div>

                                <div className="grid grid-cols-3 gap-2 md:gap-4 mt-4 pt-4 border-t">
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <Grid3x3 className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <p className="text-xl md:text-2xl font-bold text-gray-900">{item.total_blocks}</p>
                                        <p className="text-xs text-gray-500">Total</p>
                                    </div>
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <Smartphone className="h-4 w-4 text-green-600" />
                                        </div>
                                        <p className="text-xl md:text-2xl font-bold text-gray-900">{mobileBlocks}</p>
                                        <p className="text-xs text-gray-500">Mobile</p>
                                    </div>
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <Monitor className="h-4 w-4 text-purple-600" />
                                        </div>
                                        <p className="text-xl md:text-2xl font-bold text-gray-900">{webBlocks}</p>
                                        <p className="text-xs text-gray-500">Web</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Domains with Blocks Details */}
            {domainsWithBlocks && domainsWithBlocks.length > 0 && (
                <div className="space-y-4">
                    <h2 className="text-lg md:text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Detalhes dos Blocos
                    </h2>

                    {domainsWithBlocks.map((domainData) => (
                        <div key={domainData.domain.id} className="rounded-lg bg-white shadow border border-gray-200">
                            <div className="p-4 md:p-6 border-b border-gray-200">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="min-w-0">
                                        <h3 className="text-base md:text-lg font-semibold text-gray-900 truncate">
                                            {domainData.domain.name}
                                        </h3>
                                        <p className="text-sm text-gray-500">{domainData.domain.domain}</p>
                                    </div>
                                    <div className="flex items-center gap-3 sm:gap-4 justify-between sm:justify-end">
                                        <div className="text-left sm:text-right">
                                            <p className="text-xs md:text-sm text-gray-500">Total de Blocos</p>
                                            <p className="text-xl md:text-2xl font-bold text-gray-900">
                                                {domainData.total_blocks}
                                            </p>
                                        </div>
                                        {domainData.root && (
                                            <div className="flex items-center gap-2">
                                                {getStatusBadge(domainData.root.status)}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => openRootStatusDialog(domainData.root)}
                                                    title="Atualizar status do root"
                                                >
                                                    <Settings className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {domainData.blocks.length > 0 && (
                                <div className="p-4 md:p-6">
                                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                        {domainData.blocks.map((block) => {
                                            const blockTypeName = block.ad_unit_name.split('_')
                                                .find((part) =>
                                                    ['Anchor', 'Content1', 'Content2', 'Fixed', 'Interstitial',
                                                        'Rewarded', 'Topo', 'OfferWall', 'Trigger'].includes(part)
                                                ) || 'Unknown';

                                            const isMobile = block.ad_unit_name.includes('_MOBILE_');
                                            const isWeb = block.ad_unit_name.includes('_WEB_');
                                            const formatType = isMobile ? 'MOBILE' : isWeb ? 'WEB' : 'SHARED';

                                            return (
                                                <div
                                                    key={block.id}
                                                    className="p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                                                >
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            {formatType === 'MOBILE' ? (
                                                                <Smartphone className="h-4 w-4 text-green-600 flex-shrink-0" />
                                                            ) : formatType === 'WEB' ? (
                                                                <Monitor className="h-4 w-4 text-purple-600 flex-shrink-0" />
                                                            ) : (
                                                                <Globe className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                                            )}
                                                            <span className="text-sm font-medium text-gray-900 truncate">
                                                                {blockTypeName}
                                                            </span>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => openBlockStatusDialog(block)}
                                                            className="flex-shrink-0 h-6 w-6 p-0"
                                                        >
                                                            <Settings className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                    <div className="mb-2">
                                                        {getStatusBadge(block.ad_unit_status)}
                                                    </div>
                                                    <p className="text-xs text-gray-500 truncate" title={block.ad_unit_name}>
                                                        {block.ad_unit_name}
                                                    </p>
                                                    <p className="text-xs text-gray-400 mt-1 truncate">
                                                        ID: {block.ad_unit_id}
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {domainData.blocks.length === 0 && (
                                <div className="p-6 text-center text-gray-500">
                                    <p>Nenhum bloco configurado ainda.</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Empty State */}
            {summary && summary.length === 0 && (
                <div className="rounded-lg bg-white p-8 md:p-12 shadow text-center">
                    <div className="mx-auto h-12 w-12 md:h-16 md:w-16 text-gray-400 mb-4">
                        <Grid3x3 className="h-full w-full" />
                    </div>
                    <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">Nenhum bloco encontrado</h3>
                    <p className="text-sm md:text-base text-gray-600 mb-6">Comece gerando blocos para seus domínios.</p>
                    <Button
                        onClick={() => setIsGenerateDialogOpen(true)}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 w-full sm:w-auto"
                    >
                        <Zap className="mr-2 h-4 w-4" />
                        Gerar Primeiro Bloco
                    </Button>
                </div>
            )}

            {/* Generate Blocks Dialog */}
            <Dialog open={isGenerateDialogOpen} onOpenChange={setIsGenerateDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Gerar Blocos GAM</DialogTitle>
                        <DialogDescription>
                            Configure os blocos de anúncios que serão gerados no Google Ad Manager
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 mt-4">
                        <div className="space-y-2">
                            <Label htmlFor="domain">Domínio *</Label>
                            <select
                                id="domain"
                                value={selectedDomainId}
                                onChange={(e) => setSelectedDomainId(parseInt(e.target.value))}
                                disabled={generateMutation.isPending}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                <option value="0">Selecione um domínio</option>
                                {domains?.map((domain) => (
                                    <option key={domain.id} value={domain.id}>
                                        {domain.name} ({domain.domain})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <Label>Tipo de Bloco</Label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {blockTypes.map((type) => {
                                    const Icon = type.icon;
                                    return (
                                        <button
                                            key={type.value}
                                            type="button"
                                            onClick={() => setSelectedBlockType(type.value)}
                                            disabled={generateMutation.isPending}
                                            className={`
                                                p-4 rounded-lg border-2 transition-all text-left
                                                ${selectedBlockType === type.value
                                                ? 'border-green-600 bg-green-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                            }
                                                ${generateMutation.isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                            `}
                                        >
                                            <div className="flex items-center gap-2">
                                                {Icon && <Icon className="h-5 w-5 text-green-600" />}
                                                <span className="font-medium text-gray-900 text-sm md:text-base">{type.label}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {selectedBlockType !== 'ALL' && (
                            <div className="space-y-2">
                                <Label>Formato</Label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {formatTypes.map((format) => {
                                        const Icon = format.icon;
                                        return (
                                            <button
                                                key={format.value}
                                                type="button"
                                                onClick={() => setSelectedFormatType(format.value)}
                                                disabled={generateMutation.isPending}
                                                className={`
                                                    p-4 rounded-lg border-2 transition-all
                                                    ${selectedFormatType === format.value
                                                    ? 'border-green-600 bg-green-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                                }
                                                    ${generateMutation.isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                                `}
                                            >
                                                <div className="flex flex-col items-center gap-2">
                                                    <Icon className="h-6 w-6 text-green-600" />
                                                    <span className="text-sm font-medium text-gray-900 text-center">
                                                        {format.label}
                                                    </span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="rounded-lg bg-gray-50 p-4">
                            <h4 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">Resumo da Geração:</h4>
                            <ul className="space-y-1 text-sm text-gray-600">
                                <li>
                                    • <strong>Domínio:</strong>{' '}
                                    {selectedDomainId
                                        ? domains?.find(d => d.id === selectedDomainId)?.name
                                        : 'Nenhum selecionado'}
                                </li>
                                <li>
                                    • <strong>Blocos:</strong>{' '}
                                    {selectedBlockType === 'ALL'
                                        ? '15 blocos (todos)'
                                        : blockTypes.find(t => t.value === selectedBlockType)?.label}
                                </li>
                                {selectedBlockType !== 'ALL' && (
                                    <li>
                                        • <strong>Formato:</strong>{' '}
                                        {formatTypes.find(f => f.value === selectedFormatType)?.label}
                                    </li>
                                )}
                            </ul>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    setIsGenerateDialogOpen(false);
                                    resetForm();
                                }}
                                disabled={generateMutation.isPending}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleGenerate}
                                disabled={!selectedDomainId || generateMutation.isPending}
                                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                            >
                                {generateMutation.isPending ? (
                                    'Gerando...'
                                ) : (
                                    <>
                                        <Zap className="mr-2 h-4 w-4" />
                                        Gerar Blocos
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Update Block Status Dialog */}
            <Dialog open={isUpdateBlockDialogOpen} onOpenChange={setIsUpdateBlockDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Atualizar Status do Bloco</DialogTitle>
                        <DialogDescription>
                            Altere o status do bloco {selectedBlock?.ad_unit_name}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 mt-4">
                        <div className="space-y-3">
                            <Label>Selecione o novo status</Label>
                            {statusOptions.map((option) => {
                                const Icon = option.icon;
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => setSelectedStatus(option.value)}
                                        disabled={updateBlockMutation.isPending}
                                        className={`
                                            w-full p-4 rounded-lg border-2 transition-all text-left
                                            ${selectedStatus === option.value
                                            ? 'border-blue-600 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }
                                            ${updateBlockMutation.isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        `}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Icon className={`h-5 w-5 ${option.color}`} />
                                            <span className="font-medium text-gray-900">{option.label}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => setIsUpdateBlockDialogOpen(false)}
                                disabled={updateBlockMutation.isPending}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleUpdateBlockStatus}
                                disabled={updateBlockMutation.isPending}
                                className="flex-1"
                            >
                                {updateBlockMutation.isPending ? 'Atualizando...' : 'Atualizar Status'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Update Root Status Dialog */}
            <Dialog open={isUpdateRootDialogOpen} onOpenChange={setIsUpdateRootDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Atualizar Status do Root</DialogTitle>
                        <DialogDescription>
                            Altere o status do root {selectedRoot?.ad_unit_root_name}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 mt-4">
                        <div className="space-y-3">
                            <Label>Selecione o novo status</Label>
                            {statusOptions.map((option) => {
                                const Icon = option.icon;
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => setSelectedStatus(option.value)}
                                        disabled={updateRootMutation.isPending}
                                        className={`
                                            w-full p-4 rounded-lg border-2 transition-all text-left
                                            ${selectedStatus === option.value
                                            ? 'border-blue-600 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }
                                            ${updateRootMutation.isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        `}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Icon className={`h-5 w-5 ${option.color}`} />
                                            <span className="font-medium text-gray-900">{option.label}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="space-y-2">
                            <Label>Opções de Atualização</Label>
                            <div className="space-y-2">
                                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50">
                                    <input
                                        type="radio"
                                        checked={updateRootAndChildren}
                                        onChange={() => setUpdateRootAndChildren(true)}
                                        disabled={updateRootMutation.isPending}
                                        className="w-4 h-4"
                                    />
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">Root e Blocos Filhos</p>
                                        <p className="text-xs text-gray-500">Atualiza o root e todos os blocos filhos</p>
                                    </div>
                                </label>
                                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50">
                                    <input
                                        type="radio"
                                        checked={!updateRootAndChildren}
                                        onChange={() => setUpdateRootAndChildren(false)}
                                        disabled={updateRootMutation.isPending}
                                        className="w-4 h-4"
                                    />
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">Apenas Blocos Filhos</p>
                                        <p className="text-xs text-gray-500">Mantém o root e atualiza apenas os filhos</p>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => setIsUpdateRootDialogOpen(false)}
                                disabled={updateRootMutation.isPending}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleUpdateRootStatus}
                                disabled={updateRootMutation.isPending}
                                className="flex-1"
                            >
                                {updateRootMutation.isPending ? 'Atualizando...' : 'Atualizar Status'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}